"use client";

import { listAdminUsersApiV1AdminUsersGet } from "@/src/api/sdk.gen";
import { useEffect, useMemo, useState } from "react";

import { InvitationManager } from "@/components/admin/InvitationManager";
import { MemberManager } from "@/components/admin/MemberManager";
import { UserOrganizationManager } from "@/components/admin/UserOrganizationManager";

type TabId = "overview" | "users" | "organizations" | "members" | "invitations";

interface OverviewUser {
    clerkUserId: string;
    email: string | null;
    name: string;
    imageUrl?: string | null;
    createdAt: string | null;
    lastSignInAt: string | null;
    memberships: Array<{ orgId: number; orgName: string; role: "admin" | "member" }>;
}

interface UsersOverviewResponse {
    users: OverviewUser[];
    organizations: Array<{ id: number; name: string }>;
}

const tabs: Array<{ id: TabId; label: string }> = [
    { id: "overview", label: "Overview" },
    { id: "users", label: "Users / Organizations" },
    { id: "organizations", label: "Organizations" },
    { id: "members", label: "Members" },
    { id: "invitations", label: "Invitations" },
];

function tabClass(isActive: boolean): string {
    return isActive
        ? "rounded-md bg-indigo-600 text-white px-3 py-1.5 shadow-sm shadow-indigo-200"
        : "rounded-md text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 px-3 py-1.5";
}

export function InvitationControlTabs() {
    const [activeTab, setActiveTab] = useState<TabId>("overview");
    const [overviewUsers, setOverviewUsers] = useState<OverviewUser[]>([]);
    const [overviewOrganizations, setOverviewOrganizations] = useState<Array<{ id: number; name: string }>>([]);
    const [isOverviewLoading, setIsOverviewLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function loadOverviewUsers() {
            setIsOverviewLoading(true);
            try {
                const { data, error } = await listAdminUsersApiV1AdminUsersGet();
                if (error || !data || cancelled) return;
                const resData = data as unknown as UsersOverviewResponse;
                if (!cancelled) {
                    setOverviewUsers(resData.users || []);
                    setOverviewOrganizations(resData.organizations ?? []);
                }
            } finally {
                if (!cancelled) setIsOverviewLoading(false);
            }
        }

        loadOverviewUsers();
        return () => {
            cancelled = true;
        };
    }, []);

    const content = useMemo(() => {
        if (activeTab === "users") return <UserOrganizationManager />;
        if (activeTab === "organizations") {
            return (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-lg font-bold text-slate-900">Organizations</h2>
                    <p className="mt-2 text-sm text-slate-600">
                        All organizations you can manage.
                    </p>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="border-b border-slate-200">
                                <tr>
                                    <th className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">ID</th>
                                    <th className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">Organization</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isOverviewLoading ? (
                                    <tr>
                                        <td colSpan={2} className="px-3 py-6 text-sm text-slate-500">
                                            Loading organizations...
                                        </td>
                                    </tr>
                                ) : overviewOrganizations.length === 0 ? (
                                    <tr>
                                        <td colSpan={2} className="px-3 py-6 text-sm text-slate-500">
                                            No organizations found.
                                        </td>
                                    </tr>
                                ) : (
                                    overviewOrganizations.map((org) => (
                                        <tr key={org.id} className="border-b border-slate-100">
                                            <td className="px-3 py-3 text-sm text-slate-700">{org.id}</td>
                                            <td className="px-3 py-3 text-sm font-medium text-slate-900">{org.name}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }
        if (activeTab === "members") return <MemberManager />;
        if (activeTab === "invitations") return <InvitationManager />;

        const now = Date.now();
        const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
        const totalUsers = overviewUsers.length;
        const activeUsers = overviewUsers.filter((user) => {
            if (!user.lastSignInAt) return false;
            const signedInTime = new Date(user.lastSignInAt).getTime();
            return !Number.isNaN(signedInTime) && now - signedInTime <= THIRTY_DAYS_MS;
        }).length;
        const usersWithOrgAccess = overviewUsers.filter((user) => user.memberships.length > 0).length;
        const unassignedUsers = totalUsers - usersWithOrgAccess;
        const adminUsers = overviewUsers.filter((user) =>
            user.memberships.some((membership) => membership.role === "admin"),
        ).length;
        const totalMemberships = overviewUsers.reduce(
            (sum, user) => sum + user.memberships.length,
            0,
        );

        const statCards = [
            { label: "Total Users", value: totalUsers },
            { label: "Active Users (30d)", value: activeUsers },
            { label: "Total Organizations", value: overviewOrganizations.length },
            { label: "Users With Org Access", value: usersWithOrgAccess },
            { label: "Unassigned Users", value: unassignedUsers },
            { label: "Admin Users", value: adminUsers },
            { label: "Total Memberships", value: totalMemberships },
        ];

        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-bold text-slate-900">Overview</h2>
                <p className="mt-2 text-sm text-slate-600">
                    Signup and access activity from Clerk.
                </p>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {statCards.map((card) => (
                        <div
                            key={card.label}
                            className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                        >
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {card.label}
                            </p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{card.value}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="border-b border-slate-200">
                            <tr>
                                <th className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">User</th>
                                <th className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">Signup</th>
                                <th className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">Last sign in</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isOverviewLoading ? (
                                <tr>
                                    <td colSpan={3} className="px-3 py-6 text-sm text-slate-500">
                                        Loading users...
                                    </td>
                                </tr>
                            ) : overviewUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-3 py-6 text-sm text-slate-500">
                                        No users found.
                                    </td>
                                </tr>
                            ) : (
                                overviewUsers.map((user) => (
                                    <tr key={user.clerkUserId} className="border-b border-slate-100">
                                        <td className="px-3 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="h-8 w-8 overflow-hidden rounded-full border border-slate-200 bg-slate-100">
                                                    {user.imageUrl ? (
                                                        <img
                                                            src={user.imageUrl}
                                                            alt={user.name}
                                                            className="h-full w-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center text-[10px] font-bold text-slate-500">
                                                            {user.name.slice(0, 1).toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-slate-900">{user.name}</div>
                                                    <div className="text-xs text-slate-500">{user.email ?? user.clerkUserId}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 text-sm text-slate-700">
                                            {user.createdAt ? new Date(user.createdAt).toLocaleString() : "—"}
                                        </td>
                                        <td className="px-3 py-3 text-sm text-slate-700">
                                            {user.lastSignInAt ? new Date(user.lastSignInAt).toLocaleString() : "—"}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }, [activeTab, isOverviewLoading, overviewUsers, overviewOrganizations]);

    return (
        <div className="space-y-6 pt-6">
            <div className="overflow-x-auto">
                <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`text-xs font-semibold transition-all ${tabClass(activeTab === tab.id)}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {content}
        </div>
    );
}
